import create from 'zustand'
import { TransferAgreementImage, TransferAgreement } from '../types'
import {
	deleteManuallyUploadedImage,
	fetchTransferAgreementImages,
} from '../api'
import Response from '../../../../lib/http/Response'

interface TransferAgreementImagesState {
	transferAgreementImages: TransferAgreementImage[]
	selectedTransferAgreement: TransferAgreement | null
	totalTransferAgreementImagesCount: number
	pageNumber: number
	hasTransferAgreementImagesInitiallyLoaded: boolean
	searchString: string
	uploadImagesModalOpen: boolean
	setUploadImagesModalOpen: (uploadImagesModalOpen: boolean) => void
	fetchTransferAgreementImages: (
		transferAgreementId: string,
		params?: {
			isInitialLoad?: boolean
			searchString?: string
		},
	) => Promise<Response>
	deleteImageId: ({
		transferAgreementImageId,
		transferAgreementId,
	}: {
		transferAgreementImageId: string
		transferAgreementId: string
	}) => Promise<Response | undefined>
	setSearchString: (searchString: string) => void
}

export const useTransferAgreementImagesStore =
	create<TransferAgreementImagesState>((set, get) => ({
		transferAgreementImages: [],
		selectedTransferAgreement: null,
		totalTransferAgreementImagesCount: 0,
		pageNumber: 0,
		hasTransferAgreementImagesInitiallyLoaded: false,
		searchString: '',
		uploadImagesModalOpen: false,
		setUploadImagesModalOpen: (uploadImagesModalOpen: boolean) => {
			set({
				uploadImagesModalOpen,
			})
		},

		setSearchString: (searchString) => set({ searchString }),

		fetchTransferAgreementImages: async (
			transferAgreementId: string,
			params?: {
				isInitialLoad?: boolean
			},
		) => {
			const { pageNumber, transferAgreementImages, searchString } = get()

			const currentPage = params?.isInitialLoad ? 0 : pageNumber
			const perPage = 30

			const transferAgreementImagesResponse =
				await fetchTransferAgreementImages({
					transferAgreementId,
					page: currentPage,
					perPage,
					...(searchString
						? { filters: { partnerImageId: searchString } }
						: {}),
				})

			if (!transferAgreementImagesResponse.isSuccessful) {
				set({
					transferAgreementImages: [],
					totalTransferAgreementImagesCount: 0,
					pageNumber: 0,
					searchString: '',
					uploadImagesModalOpen: false,
					selectedTransferAgreement: null,
				})
				return transferAgreementImagesResponse
			}

			const { data, pagination } =
				transferAgreementImagesResponse.data.transferAgreementImageData
			const { transferAgreement } = transferAgreementImagesResponse.data
			const { totalItems } = pagination

			const newImages = params?.isInitialLoad
				? data
				: [...transferAgreementImages, ...data]

			set({
				transferAgreementImages: newImages,
				totalTransferAgreementImagesCount: totalItems,
				pageNumber: currentPage + 1,
				hasTransferAgreementImagesInitiallyLoaded: true,
				uploadImagesModalOpen: false,
				selectedTransferAgreement: transferAgreement,
			})

			return transferAgreementImagesResponse
		},
		deleteImageId: async ({
			transferAgreementImageId,
			transferAgreementId,
		}: {
			transferAgreementImageId: string
			transferAgreementId: string
		}) => {
			const { transferAgreementImages, totalTransferAgreementImagesCount } =
				get()

			const response = await deleteManuallyUploadedImage({
				transferAgreementImageId,
				transferAgreementId,
			})

			if (response.isSuccessful) {
				const newImages = transferAgreementImages.filter(
					(image) =>
						image.transfer_agreement_image_id !== transferAgreementImageId,
				)
				const newTotalCount = totalTransferAgreementImagesCount - 1

				set({
					transferAgreementImages: newImages,
					totalTransferAgreementImagesCount: newTotalCount,
				})
			}
			return response
		},
	}))
